import React from 'react';
import { Flame, CheckCircle2, XCircle } from 'lucide-react';

/**
 * StockBadge — customer-facing stock indicator.
 *
 * Rules:
 *  - stock <= 0           → "Out of stock"
 *  - 0 < stock < THRESHOLD → "Hurry! Only a few left" (FOMO, animated)
 *  - stock >= THRESHOLD   → "In stock" (no count exposed) — or null if showWhenAmple={false}
 *
 * The exact unit count is intentionally NOT shown to customers. Showing
 * "12 in stock" reads like a hardware-store inventory list and undercuts
 * the urgency we want when supply is genuinely tight.
 */
export const LOW_STOCK_THRESHOLD = 5;

const StockBadge = ({
  stock,
  showWhenAmple = true,
  variant = 'default', // 'default' | 'inline' | 'compact'
  className = '',
}) => {
  const qty = Number.isFinite(+stock) ? +stock : 0;

  // Out of stock
  if (qty <= 0) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full bg-red-50 text-red-700 border border-red-200 px-2.5 py-1 text-xs font-semibold ${className}`}
        data-testid="stock-badge-oos"
      >
        <XCircle className="h-3.5 w-3.5" />
        Out of stock
      </span>
    );
  }

  // Few left → FOMO badge
  if (qty < LOW_STOCK_THRESHOLD) {
    if (variant === 'inline') {
      return (
        <span
          className={`text-[10px] font-semibold text-orange-600 ${className}`}
          data-testid="stock-badge-low-inline"
        >
          Hurry! Few left
        </span>
      );
    }
    return (
      <span
        className={`relative inline-flex items-center gap-1.5 overflow-hidden rounded-full bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 text-white px-3 py-1 text-xs font-bold shadow-sm shadow-orange-200 ${className}`}
        data-testid="stock-badge-low"
      >
        {/* Subtle shine sweep on hover */}
        <span className="pointer-events-none absolute inset-0 -translate-x-full hover:translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-[1200ms] ease-out" />
        <span className="relative flex items-center justify-center">
          <span className="absolute inline-flex h-2 w-2 rounded-full bg-white/80 animate-ping" />
          <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-white" />
        </span>
        <Flame className="relative h-3.5 w-3.5 fill-white/30" />
        <span className="relative tracking-wide">
          Hurry! Only a few left
        </span>
      </span>
    );
  }

  // Plenty in stock — show neutral confirmation, or hide entirely
  if (!showWhenAmple) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 px-2.5 py-1 text-xs font-medium ${className}`}
      data-testid="stock-badge-in"
    >
      <CheckCircle2 className="h-3.5 w-3.5" />
      In stock
    </span>
  );
};

export default StockBadge;
